export const AGENT_CONTEXT_SCHEMA = "pauta.agent-context.v1";

const BLOCK_PREFIX = "<!-- pauta:agent-context:v1\n";
const BLOCK_SUFFIX = "\n-->";
const SEPARATOR = "\n\n";

export type AgentContext = Record<string, unknown>;

export interface IssueHumanFields {
  status: string;
  priority: string;
  due_date: string | null;
  assignee: { id: string; name: string } | null;
  requester: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  synced_at: string;
}

const STATUS_COPY: Record<string, string> = {
  backlog: "No backlog — ainda não priorizada para execução.",
  todo: "A fazer — pronta ou aguardando definição de execução.",
  in_progress: "Em andamento — há execução ativa.",
  in_review: "Aguardando revisão, decisão ou ação humana.",
  done: "Concluída — o histórico abaixo registra o que foi entregue.",
  canceled: "Cancelada — preservada para manter o histórico da decisão.",
};

const PRIORITY_COPY: Record<string, string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Média",
  low: "Baixa",
  none: "Não definida",
};

function isPlainObject(value: unknown): value is AgentContext {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, nested]) => [key, stableValue(nested)])
  );
}

function mergeContext(current: AgentContext, incoming: AgentContext): AgentContext {
  const merged: AgentContext = { ...current };
  for (const [key, value] of Object.entries(incoming)) {
    const previous = merged[key];
    merged[key] = isPlainObject(previous) && isPlainObject(value)
      ? mergeContext(previous, value)
      : value;
  }
  return merged;
}

function splitAgentContext(markdown: string): {
  body: string;
  context: AgentContext | null;
} {
  const marker = `${SEPARATOR}${BLOCK_PREFIX}`;
  const markerIndex = markdown.lastIndexOf(marker);
  const start = markerIndex >= 0 ? markerIndex + SEPARATOR.length : 0;

  if (start === 0 && !markdown.startsWith(BLOCK_PREFIX)) {
    return { body: markdown, context: null };
  }

  const block = markdown.slice(start);
  if (!block.startsWith(BLOCK_PREFIX) || !block.endsWith(BLOCK_SUFFIX)) {
    return { body: markdown, context: null };
  }

  const json = block.slice(BLOCK_PREFIX.length, -BLOCK_SUFFIX.length);
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!isPlainObject(parsed)) return { body: markdown, context: null };
    return {
      body: start === 0 ? "" : markdown.slice(0, markerIndex),
      context: parsed,
    };
  } catch {
    return { body: markdown, context: null };
  }
}

/**
 * Reads the versioned machine context stored at the end of a markdown body.
 * Invalid or hand-written lookalike blocks are treated as ordinary content.
 */
export function readAgentContext(markdown: string | null | undefined): AgentContext | null {
  return splitAgentContext(markdown ?? "").context;
}

/** Returns the human-visible markdown without the trailing machine context block. */
export function stripAgentContext(markdown: string | null | undefined): string {
  return splitAgentContext(markdown ?? "").body;
}

/**
 * Adds or merges machine-readable context without modifying any human-authored
 * markdown. Repeated calls are idempotent and keep JSON key order stable so
 * activity diffs stay readable.
 */
export function writeAgentContext(
  markdown: string | null | undefined,
  incoming: AgentContext
): string {
  if (!isPlainObject(incoming)) {
    throw new TypeError("agent_context must be a JSON object");
  }

  const { body, context } = splitAgentContext(markdown ?? "");
  const merged = stableValue({
    ...mergeContext(context ?? {}, incoming),
    schema: AGENT_CONTEXT_SCHEMA,
  });
  const json = JSON.stringify(merged, null, 2);
  if (new TextEncoder().encode(json).length > 65_536) {
    throw new RangeError("agent_context must be 64 KB or smaller");
  }
  const block = `${BLOCK_PREFIX}${json}${BLOCK_SUFFIX}`;
  return body ? `${body}${SEPARATOR}${block}` : block;
}

/** Keeps the human status panel and machine context aligned with canonical fields. */
export function syncIssueHumanFields(
  markdown: string | null | undefined,
  fields: IssueHumanFields
): string {
  const { body, context } = splitAgentContext(markdown ?? "");
  const replacements: Array<[RegExp, string]> = [
    [/^- 🔄 \*\*Status:\*\*.*$/m, `- 🔄 **Status:** ${STATUS_COPY[fields.status] ?? fields.status}`],
    [/^- 🚦 \*\*Prioridade:\*\*.*$/m, `- 🚦 **Prioridade:** ${PRIORITY_COPY[fields.priority] ?? fields.priority}`],
    [/^- 🗓️ \*\*Prazo:\*\*.*$/m, `- 🗓️ **Prazo:** ${fields.due_date ? fields.due_date.slice(0, 10) : "sem prazo estruturado"}`],
    [/^- 🙋 \*\*Responsável:\*\*.*$/m, `- 🙋 **Responsável:** ${fields.assignee?.name ?? "não registrado"}`],
    [/^- 🧾 \*\*Origem do pedido:\*\*.*$/m, `- 🧾 **Origem do pedido:** ${fields.requester?.name ?? "não registrada"}`],
    [/^- 📁 \*\*Projeto:\*\*.*$/m, `- 📁 **Projeto:** ${fields.project?.name ?? "escopo transversal ou ainda não associado"}`],
  ];
  const updatedBody = replacements.reduce(
    (current, [pattern, replacement]) => current.replace(pattern, replacement),
    body
  );
  return writeAgentContext(
    updatedBody,
    mergeContext(context ?? {}, {
      status: {
        value: fields.status,
        explanation: STATUS_COPY[fields.status] ?? fields.status,
        priority: fields.priority,
        due_date: fields.due_date,
      },
      ownership: {
        assignee: fields.assignee,
        requester: fields.requester,
      },
      organization: { project: fields.project },
      documentation: { canonical_fields_synced_at: fields.synced_at },
    })
  );
}
